import { io } from 'socket.io-client'
import { environmentConfigurations } from '@configurations/environmentConfigurations'
import { logger } from '@logger'
import { notify } from '@/core/toast'
import store from '@/store/store'
import {
  setSocketConnected,
  setSocketConnecting,
  setSocketDisconnected,
  setSocketError,
  setSocketReconnecting,
  resetSocket,
} from '@slices/socketSlice'
import { NATIVE_EVENTS } from './socketEvents'

const MODULE = 'Socket Connection'

// Stable id so the loading toast resolves in place (loading -> success/error),
// mirroring the player-session toast in useGameBootstrap.
const SOCKET_TOAST_ID = 'socket-connection'

// ── The single shared Socket.IO instance (singleton) ────────────────────────
// Module-scoped, so every import sees the same connection. `connectSocket`
// never creates a second one, and `getSocket` is the accessor everyone uses.
let socket = null

// Auth handshake failures are terminal — no amount of retrying fixes a bad
// token, so we stop the reconnection loop when we see one.
const TERMINAL_AUTH_ERRORS = ['Authentication required', 'Invalid token', 'gameCode missing from token']

// Reconnection is delegated to Socket.IO's built-in engine. Tweak here only.
const RECONNECTION_OPTIONS = {
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  randomizationFactor: 0.5,
  timeout: 20000,
}

export const getSocket = () => socket
export const isConnected = () => Boolean(socket?.connected)

// Native lifecycle -> socketSlice. Bound once per created instance.
const bindNativeEvents = (instance) => {
  instance.on(NATIVE_EVENTS.CONNECT, () => {
    logger.success(MODULE, 'Connected', { id: instance.id })
    store.dispatch(setSocketConnected({ socketId: instance.id, connectedAt: Date.now() }))
    notify('success', { id: SOCKET_TOAST_ID, title: 'Connected', sub: "You're in the game" })
  })

  instance.on(NATIVE_EVENTS.DISCONNECT, (reason) => {
    logger.warn(MODULE, 'Disconnected', { reason })
    store.dispatch(setSocketDisconnected({ reason }))
    // 'io client disconnect' = our own teardown (stopSocket) — stay silent.
    // Anything else is an unexpected drop that Socket.IO will retry.
    if (reason !== 'io client disconnect') {
      notify('loading', {
        id: SOCKET_TOAST_ID,
        title: 'Reconnecting',
        sub: 'Connection lost — restoring…',
      })
    }
  })

  instance.on(NATIVE_EVENTS.CONNECT_ERROR, (error) => {
    const message = error?.message || 'Connection error'
    logger.error(MODULE, 'Connection error', message)
    store.dispatch(setSocketError({ error: message }))

    if (TERMINAL_AUTH_ERRORS.includes(message)) {
      logger.error(MODULE, 'Auth failure is terminal — halting reconnection', message)
      notify('error', { id: SOCKET_TOAST_ID, title: 'Connection failed', sub: message })
      instance.disconnect() // stop the retry loop; needs a fresh accessToken
    }
  })

  instance.on(NATIVE_EVENTS.RECONNECT_ATTEMPT, (attempt) => {
    logger.info(MODULE, 'Reconnecting…', { attempt })
    store.dispatch(setSocketReconnecting())
  })

  instance.on(NATIVE_EVENTS.RECONNECT, (attempt) => {
    // `connect` also fires and flips status to CONNECTED; this is just a log.
    logger.success(MODULE, 'Reconnected', { attempt })
  })

  instance.on(NATIVE_EVENTS.RECONNECT_FAILED, () => {
    logger.error(MODULE, 'Reconnection failed')
    store.dispatch(setSocketError({ error: 'Reconnection failed' }))
    notify('error', {
      id: SOCKET_TOAST_ID,
      title: 'Connection failed',
      sub: 'Unable to reconnect. Please refresh.',
    })
  })
}

/**
 * Create the singleton (or reuse it). Safe to call repeatedly — e.g. React
 * StrictMode double-mounts — it will never spawn a second connection.
 *
 * @param {{ accessToken: string }} params engine JWT (must carry `gameCode`)
 * @returns {import('socket.io-client').Socket | null}
 */
export const connectSocket = ({ accessToken } = {}) => {
  if (socket) {
    logger.info(MODULE, 'Reusing existing socket instance')
    if (!socket.connected) socket.connect()
    return socket
  }

  if (!accessToken) {
    logger.error(MODULE, 'Cannot connect: accessToken is required')
    return null
  }

  store.dispatch(setSocketConnecting())
  notify('loading', { id: SOCKET_TOAST_ID, title: 'Connecting', sub: 'Joining the game…' })

  socket = io(environmentConfigurations.crashGameSocketBaseURL, {
    path: environmentConfigurations.crashGameSocketPath,
    transports: ['websocket', 'polling'],
    auth: { token: accessToken },
    ...RECONNECTION_OPTIONS,
  })

  bindNativeEvents(socket)
  logger.info(MODULE, 'Socket instance created', {
    url: environmentConfigurations.crashGameSocketBaseURL,
  })
  return socket
}

// Tear the singleton down completely and reset connection state.
export const disconnectSocket = () => {
  if (!socket) return
  logger.info(MODULE, 'Disconnecting socket')
  socket.disconnect()
  socket.removeAllListeners()
  socket = null
  store.dispatch(resetSocket())
}
